import c3 from 'c3';
import 'c3/c3.min.css';
import csv from '@/csv';
import { MONTHS } from '@/settings';
import InfoOverlay from '@/components/InfoOverlay.vue';

const R1_COLOR = '#FE6A6A';
const R2_COLOR = '#FECF67';
const R3_COLOR = '#6CD68D';
const R4_COLOR = '#6DECF9';

const ALL_PLOTS = [
  'r1max',
  'r1min',
  'r1avg',
  'r1minmax',

  'r2max',
  'r2min',
  'r2avg',
  'r2minmax',

  'r3max',
  'r3min',
  'r3avg',
  'r3minmax',

  'r4max',
  'r4min',
  'r4avg',
  'r4minmax',
];

export default {
  name: 'Chart',

  components: {
    InfoOverlay,
  },

  props: {
    watershed_id: {
      required: true,
    },
    variable: {
      type: String,
      required: true,
    },
    range: {
      type: String,
    },
  },

  data() {
    return {
      chart: null,
      offset: 0,
      showInfo: false,
      resizeListener: () => {
        const newSize = {
          width: document.querySelector('.chart-info-container').offsetWidth,
          height: document.querySelector('.chart-info-container').offsetHeight,
        };

        this.chart.resize(newSize);
      },
    };
  },

  computed: {
    chunklink() {
      return `/csvchunks/${this.watershed_id}.zip`;
    },
  },

  mounted() {
    this.getChartInfo((columns) => {
      const that = this;

      this.chart = c3.generate({
        bindto: '#chart-slot',
        size: {
          width: document.querySelector('.chart-info-container').offsetWidth,
          height: document.querySelector('.chart-info-container').offsetHeight,
        },
        data: {
          type: 'spline',
          types: {
            r1min: 'area-spline',
            r1minmax: 'area-spline',
            r2min: 'area-spline',
            r2minmax: 'area-spline',
            r3min: 'area-spline',
            r3minmax: 'area-spline',
            r4min: 'area-spline',
            r4minmax: 'area-spline',
          },
          groups: [
            ['r1minmax', 'r1min'],
            ['r2minmax', 'r2min'],
            ['r3minmax', 'r3min'],
            ['r4minmax', 'r4min'],
          ],
          order: null,
          columns,
          color: (color, d) => {
            const options = {
              r1max: R1_COLOR,
              r1min: R1_COLOR,
              r1avg: R1_COLOR,
              r1minmax: R1_COLOR,

              r2max: R2_COLOR,
              r2min: R2_COLOR,
              r2avg: R2_COLOR,
              r2minmax: R2_COLOR,

              r3max: R3_COLOR,
              r3min: R3_COLOR,
              r3avg: R3_COLOR,
              r3minmax: R3_COLOR,

              r4max: R4_COLOR,
              r4min: R4_COLOR,
              r4avg: R4_COLOR,
              r4minmax: R4_COLOR,
            };

            if (Object.prototype.hasOwnProperty.call(options, d)) {
              return options[d];
            }

            return options[d.id];
          },
        },
        legend: {
          show: false,
        },
        axis: {
          x: {
            label: 'mes',
            tick: {
              format: x => MONTHS[x],
            },
          },
          y: {
            label: 'mm',
            tick: {
              format: y => (y - this.offset).toFixed(0),
            },
          },
        },
        tooltip: {
          format: {
            value(value) {
              return value.toFixed(2);
            },
          },
          contents(d) {
            let month = null;
            const r = {};

            d.forEach((item) => {
              if (month === null) {
                month = MONTHS[item.x];
              }

              const objkey = item.id.slice(0, 2);

              if (!Object.hasOwnProperty.call(r, objkey)) {
                r[objkey] = {};
              }

              r[objkey][item.id.slice(2)] = item.value;
            });

            let output = `<table class="chart-popup">
              <tr>
                <th colspan="4" class="popup-title">${month}</th>
              </tr>
              <tr>
                <th></th>
                <th>Actual</th>
                <th>Máx</th>
                <th>Mín</th>
              </tr>`;

            for (let i = 4; i > 0; i -= 1) {
              const rowname = `r${i}`;

              if (Object.hasOwnProperty.call(r, rowname)) {
                const row = r[rowname];

                const max = (row.max - that.offset).toFixed(2);
                const avg = (row.avg - that.offset).toFixed(2);
                const min = (row.min - that.offset).toFixed(2);

                output += `<tr>
                  <td><div class="legend-square legend-r${i}"></div></td>
                  <td>${avg}</td>
                  <td>${max}</td>
                  <td>${min}</td>
                </tr>`;
              }
            }

            return `${output}</table>`;
          },
        },
      });

      window.addEventListener('resize', this.resizeListener, { passive: true });
    });
  },

  beforeDestroy() {
    window.addEventListener('resize', this.resizeListener, { passive: true });
  },

  watch: {
    watershed_id() {
      this.getChartInfo((columns) => {
        this.chart.load({
          columns,
          unload: ALL_PLOTS,
        });
      });
    },

    variable(value) {
      this.chart.axis.labels({
        y: value === 'prec' ? 'mm' : '°Centígrados',
      });

      this.getChartInfo((columns) => {
        this.chart.load({
          columns,
          unload: ALL_PLOTS,
        });
      });
    },

    range(value) {
      if (value === 'all') {
        this.chart.show(ALL_PLOTS);
      } else {
        this.chart.hide(ALL_PLOTS);
        this.chart.show([
          `r${value}max`,
          `r${value}min`,
          `r${value}avg`,
          `r${value}minmax`,
        ]);
      }
    },
  },

  methods: {
    getChartInfo(callback) {
      Promise.all([
        fetch(`data/${this.watershed_id}/${this.variable}.csv`)
          .then(response => response.text()),
        fetch(`data/${this.watershed_id}/${this.variable}_base.csv`)
          .then(response => response.text()),
      ]).then((data) => {
        const [models, base] = data.map(file => csv(file, ['avg', 'min', 'max']));
        const precolumns = {};

        /* Here we are going to do an extremely dangerous trick where we
         * compute the minimum value of the models, and if it is below cero
         * then the whole chart will be translated to non-negative values and
         * labels will be placed on the chart to counteract the efect of
         * translation. This is in order to fix a problen with c3js not
         * really supporting the area between curves
         *
         * https://github.com/c3js/c3/issues/2625
         */
        const minValue = models
          .map(m => m.min)
          .reduce((acc, cur) => Math.min(acc, cur), Infinity);

        if (minValue < 0) {
          this.offset = -minValue;
        } else {
          this.offset = 0;
        }

        base.forEach((item) => {
          const month = parseInt(item.month, 10) - 1;
          const colkey = `r${item.range}avg`;

          if (!Object.hasOwnProperty.call(precolumns, colkey)) {
            precolumns[colkey] = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
          }

          precolumns[colkey][month] = item.avg + this.offset;
        });

        models.forEach((item) => {
          const month = parseInt(item.month, 10) - 1;
          const colkeymax = `r${item.range}max`;
          const colkeymin = `r${item.range}min`;
          const colkeyminmax = `r${item.range}minmax`;

          [colkeymax, colkeymin, colkeyminmax].forEach((colkey) => {
            if (!Object.hasOwnProperty.call(precolumns, colkey)) {
              precolumns[colkey] = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
            }
          });

          precolumns[colkeymax][month] = item.max + this.offset;
          precolumns[colkeymin][month] = item.min + this.offset;
          precolumns[colkeyminmax][month] = item.max - item.min;
        });

        const columns = [];

        Object.getOwnPropertyNames(precolumns).forEach((key) => {
          columns.push(Array.prototype.concat([key], precolumns[key]));
        });

        callback(columns);
      });
    },
  },
};
